import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CreateTextHunt({ user }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [textContent, setTextContent] = useState('');
  const [questions, setQuestions] = useState([]);
  const [autoCorrectAI, setAutoCorrectAI] = useState(false);
  const [gradingStrictness, setGradingStrictness] = useState('normal');
  const [showTextDuringQuestions, setShowTextDuringQuestions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [showCourseSelector, setShowCourseSelector] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [categories, setCategories] = useState([]);

  // Load teacher's courses and categories on mount
  React.useEffect(() => {
    fetchCourses();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories/${user.id}`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/classroom/courses/${user.id}`);
      setAvailableCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      // Silently fail if not connected to Classroom
    } finally {
      setLoadingCourses(false);
    }
  };

  const addQuestion = (type) => {
    const newQuestion = {
      question_id: `q_${Date.now()}`,
      question_text: '',
      question_type: type,
      options: type === 'multiple_choice' ? ['', '', '', ''] : null,
      correct_answer_index: type === 'multiple_choice' ? 0 : null,
      model_answer: type === 'short_answer' ? '' : null,
      time_limit: type === 'multiple_choice' ? 30 : 60,
      points: 100
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const updateOption = (qIndex, optIndex, value) => {
    const updated = [...questions];
    updated[qIndex].options[optIndex] = value;
    setQuestions(updated);
  };

  const deleteQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !textContent || questions.length === 0) {
      alert('Por favor completa título, texto y al menos una pregunta');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/texthunt`, {
        title,
        description,
        teacher_id: user.id,
        teacher_name: user.name,
        text_content: textContent,
        questions,
        total_points: 1000,
        auto_correct_ai: autoCorrectAI,
        grading_strictness: gradingStrictness,
        show_text_during_questions: showTextDuringQuestions,
        category_id: categoryId,
        is_public: isPublic,
        assigned_courses: isPublic ? [] : assignedCourses
      });

      alert('✅ TextHunt creado correctamente!');
      navigate('/teacher');
    } catch (error) {
      alert('Error al crear TextHunt: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWithAI = async (numMultiple, numShort) => {
    if (!textContent.trim()) {
      alert('Primero ingresa el texto base');
      return;
    }

    setLoading(true);
    try {
      console.log('Generating TextHunt with AI...', {
        backend: BACKEND_URL,
        textLength: textContent.length,
        numMultiple,
        numShort
      });
      
      const response = await axios.post(`${BACKEND_URL}/api/ai/generate-texthunt`, {
        teacher_id: user.id,
        teacher_name: user.name,
        text_content: textContent,
        num_multiple_choice: numMultiple,
        num_short_answer: numShort,
        difficulty: 'medium',
        language: 'español'
      });

      console.log('AI Generation response:', response.data);

      const generated = response.data.texthunt;
      setTitle(generated.title);
      setDescription(generated.description);
      setQuestions(generated.questions);
      setShowAIModal(false);
      alert(`✅ ${generated.questions.length} preguntas generadas con IA!`);
    } catch (error) {
      console.error('AI Generation error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      let errorMsg = 'Error desconocido';
      if (error.response) {
        errorMsg = error.response.data?.detail || `Error ${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
        errorMsg = 'No se recibió respuesta del servidor';
      } else {
        errorMsg = error.message;
      }
      
      alert('Error al generar con IA: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📚 Crear TextHunt</h1>
              <p className="text-gray-600 mt-2">Actividad de comprensión lectora con preguntas mixtas</p>
            </div>
            <button
              onClick={() => navigate('/teacher')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              ← Volver
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Información Básica */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Información Básica</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Título *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Comprensión de El Quijote"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Descripción</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="2"
                placeholder="Breve descripción de la actividad"
              />
            </div>
          </div>

          {/* Texto Base */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Texto Base *</h2>
              <button
                type="button"
                onClick={() => setShowAIModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!textContent.trim()}
                title={!textContent.trim() ? "Primero ingresa el texto base" : ""}
              >
                ✨ Generar Preguntas con IA
              </button>
            </div>
            
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              rows="12"
              placeholder="Ingresa aquí el texto que los estudiantes leerán...&#10;&#10;Puede ser un artículo, fragmento literario, noticia, etc."
              required
            />
            <div className="flex items-start gap-2 text-sm text-gray-500 mt-2">
              <span>💡</span>
              <div>
                <p>Los estudiantes verán este texto mientras responden las preguntas.</p>
                <p className="text-purple-600 font-medium mt-1">
                  Después de ingresar el texto, usa el botón "✨ Generar Preguntas con IA" para crear preguntas automáticamente.
                </p>
              </div>
            </div>
          </div>

          {/* Preguntas */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Preguntas ({questions.length})</h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => addQuestion('multiple_choice')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  + Opción Múltiple
                </button>
                <button
                  type="button"
                  onClick={() => addQuestion('short_answer')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  + Respuesta Corta
                </button>
              </div>
            </div>

            {questions.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>No hay preguntas aún</p>
                <p className="text-sm mt-2">Agrégalas manualmente o usa "Generar con IA"</p>
              </div>
            )}

            {questions.map((q, qIndex) => (
              <div key={q.question_id} className="border rounded-lg p-4 mb-4 bg-gray-50">
                <div className="flex justify-between items-start mb-3">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {q.question_type === 'multiple_choice' ? '☑️ Opción Múltiple' : '📝 Respuesta Corta'}
                  </span>
                  <button
                    type="button"
                    onClick={() => deleteQuestion(qIndex)}
                    className="text-red-600 hover:text-red-800"
                  >
                    🗑️
                  </button>
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium mb-1">Pregunta *</label>
                  <input
                    type="text"
                    value={q.question_text}
                    onChange={(e) => updateQuestion(qIndex, 'question_text', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="¿Cuál es la pregunta?"
                    required
                  />
                </div>

                {q.question_type === 'multiple_choice' && (
                  <div className="mb-3">
                    <label className="block text-sm font-medium mb-2">Opciones *</label>
                    {q.options.map((opt, optIndex) => (
                      <div key={optIndex} className="flex items-center gap-2 mb-2">
                        <input
                          type="radio"
                          name={`correct_${qIndex}`}
                          checked={q.correct_answer_index === optIndex}
                          onChange={() => updateQuestion(qIndex, 'correct_answer_index', optIndex)}
                          className="w-4 h-4"
                        />
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => updateOption(qIndex, optIndex, e.target.value)}
                          className="flex-1 px-3 py-2 border rounded-lg"
                          placeholder={`Opción ${optIndex + 1}`}
                          required
                        />
                      </div>
                    ))}
                    <p className="text-xs text-gray-500 mt-1">Selecciona la opción correcta</p>
                  </div>
                )}

                {q.question_type === 'short_answer' && (
                  <div className="mb-3">
                    <label className="block text-sm font-medium mb-1">
                      Respuesta Modelo (Opcional)
                      <span className="text-xs text-gray-500 ml-2">
                        No es necesaria - ChatGPT evaluará con su propio criterio
                      </span>
                    </label>
                    <textarea
                      value={q.model_answer}
                      onChange={(e) => updateQuestion(qIndex, 'model_answer', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows="2"
                      placeholder="Opcional: Puedes dejar vacío, ChatGPT evaluará según el contexto"
                    />
                  </div>
                )}

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Tiempo (seg)</label>
                    <input
                      type="number"
                      value={q.time_limit}
                      onChange={(e) => updateQuestion(qIndex, 'time_limit', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="10"
                      max="300"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Puntos</label>
                    <input
                      type="number"
                      value={q.points}
                      onChange={(e) => updateQuestion(qIndex, 'points', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="0"
                      max="1000"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Configuración */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Configuración</h2>
            
            {/* Category Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🗂️ Categoría (opcional)
              </label>
              <select
                value={categoryId || ''}
                onChange={(e) => setCategoryId(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Sin categoría</option>
                {categories.filter(cat => !cat.parent_id).map(category => (
                  <optgroup key={category.id} label={`${category.icon} ${category.name}`}>
                    <option value={category.id}>
                      {category.icon} {category.name}
                    </option>
                    {categories.filter(sub => sub.parent_id === category.id).map(subcat => (
                      <option key={subcat.id} value={subcat.id}>
                        &nbsp;&nbsp;{subcat.icon} {subcat.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="autoCorrectAI"
                checked={autoCorrectAI}
                onChange={(e) => setAutoCorrectAI(e.target.checked)}
                className="w-5 h-5"
              />
              <label htmlFor="autoCorrectAI" className="text-sm">
                <strong>Corrección automática con IA</strong> de respuestas cortas
                <p className="text-gray-500 text-xs mt-1">
                  Si está desactivado, deberás corregir manualmente las respuestas cortas
                </p>
              </label>
            </div>

            {/* Nivel de exigencia */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
              <label className="block text-sm font-medium mb-3">
                Nivel de Exigencia en la Corrección
              </label>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="strictness"
                    value="lenient"
                    checked={gradingStrictness === 'lenient'}
                    onChange={(e) => setGradingStrictness(e.target.value)}
                    className="mr-2"
                  />
                  <div>
                    <span className="font-semibold">😊 Flexible</span>
                    <p className="text-xs text-gray-600">Para estudiantes de ciclo medio (10-12 años). Valora la idea general más que la precisión.</p>
                  </div>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="strictness"
                    value="normal"
                    checked={gradingStrictness === 'normal'}
                    onChange={(e) => setGradingStrictness(e.target.value)}
                    className="mr-2"
                  />
                  <div>
                    <span className="font-semibold">⚖️ Normal</span>
                    <p className="text-xs text-gray-600">Para estudiantes de secundaria. Equilibrio entre comprensión y precisión.</p>
                  </div>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="strictness"
                    value="strict"
                    checked={gradingStrictness === 'strict'}
                    onChange={(e) => setGradingStrictness(e.target.value)}
                    className="mr-2"
                  />
                  <div>
                    <span className="font-semibold">🎓 Exigente</span>
                    <p className="text-xs text-gray-600">Para estudiantes avanzados. Requiere respuestas completas y precisas.</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Mostrar texto durante preguntas */}
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTextDuringQuestions}
                  onChange={(e) => setShowTextDuringQuestions(e.target.checked)}
                  className="mr-3 w-5 h-5"
                />
                <div>
                  <span className="font-semibold">📖 Mostrar texto durante las preguntas</span>
                  <p className="text-xs text-gray-600 mt-1">
                    Si está activado, los estudiantes podrán ver el texto mientras responden. 
                    Si está desactivado, tendrán que responder de memoria.
                  </p>
                </div>
              </label>
            </div>

            {/* Asignación de clase */}
            <div className="border-t pt-4 mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">Asignación</label>
              
              {/* Public TextHunt Checkbox */}
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={isPublic}
                  onChange={(e) => {
                    setIsPublic(e.target.checked);
                    if (e.target.checked) {
                      setAssignedCourses([]);
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_public" className="ml-2 text-sm text-gray-700">
                  <strong>TextHunt Público</strong> - Cualquier estudiante puede acceder
                </label>
              </div>

              {/* Course Assignment */}
              {!isPublic && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowCourseSelector(!showCourseSelector)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {assignedCourses.length > 0 
                      ? `${assignedCourses.length} clase(s) asignada(s)` 
                      : 'Asignar a clases'}
                  </button>

                  {showCourseSelector && (
                    <div className="mt-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      {loadingCourses ? (
                        <p className="text-sm text-gray-600">Cargando clases...</p>
                      ) : availableCourses.length === 0 ? (
                        <p className="text-sm text-gray-600">
                          No hay clases disponibles. Conecta tu cuenta de Google Classroom primero.
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {availableCourses.map((course) => (
                            <div key={course.id} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`course_${course.id}`}
                                checked={assignedCourses.includes(course.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setAssignedCourses([...assignedCourses, course.id]);
                                  } else {
                                    setAssignedCourses(assignedCourses.filter(id => id !== course.id));
                                  }
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`course_${course.id}`} className="ml-2 text-sm text-gray-700">
                                {course.name} {course.section && `(${course.section})`}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected Courses Display */}
                  {assignedCourses.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {assignedCourses.map((courseId) => {
                        const course = availableCourses.find(c => c.id === courseId);
                        return course ? (
                          <span key={courseId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                            {course.name}
                            <button
                              type="button"
                              onClick={() => {
                                setAssignedCourses(assignedCourses.filter(id => id !== courseId));
                              }}
                              className="ml-2 hover:text-blue-600"
                            >
                              ×
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/teacher')}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
            >
              {loading ? 'Creando...' : '✅ Crear TextHunt'}
            </button>
          </div>
        </form>

        {/* Modal de Generación con IA */}
        {showAIModal && (
          <AIGenerateModal
            onClose={() => setShowAIModal(false)}
            onGenerate={handleGenerateWithAI}
          />
        )}
      </div>
    </div>
  );
}

function AIGenerateModal({ onClose, onGenerate }) {
  const [numMultiple, setNumMultiple] = useState(5);
  const [numShort, setNumShort] = useState(5);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4">🤖 Generar Preguntas con IA</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Preguntas de Opción Múltiple</label>
          <input
            type="number"
            value={numMultiple}
            onChange={(e) => setNumMultiple(parseInt(e.target.value))}
            className="w-full px-3 py-2 border rounded-lg"
            min="0"
            max="20"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Preguntas de Respuesta Corta</label>
          <input
            type="number"
            value={numShort}
            onChange={(e) => setNumShort(parseInt(e.target.value))}
            className="w-full px-3 py-2 border rounded-lg"
            min="0"
            max="20"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Cancelar
          </button>
          <button
            onClick={() => onGenerate(numMultiple, numShort)}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            ✨ Generar
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateTextHunt;
